import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Button } from '@mui/material';
import InputImage from '../../../components/Form/InputImage';
import { ImagesContext } from '../../../contexts/ImagesContext';
import { ImageType, ImageTypeValue } from '../../../utils/image';
import Paginator from '../../../components/Paginator';
import DeleteModal from '../../../components/Modals/DeleteModal/DeleteModal';
import Img from '../../../components/Img';
import config from '../../../config';

interface Props {
  type: ImageType
}

const PER_PAGE = 15;

const ImagesManagement = (props: Props) => {
  // states
  const [deletingImage, setDeletingImage] = useState(null);

  const { type } = props;
  const {
    apiError,
    validationError,
    GetImages,
    images,
    total,
    links,
    currentPage,
    fromItem,
    toItem,
    destroyImage,
    reset,
  } = useContext(ImagesContext);

  const [inputValidationErrorMessage, setInputValidationErrorMessage] = useState<string | null>(null);

  const title = useMemo(() => {
    if (type === ImageTypeValue.SYSTEM_IMAGE) {
      return 'システム画像一覧';
    }
    return 'ブロック画像一覧';
  }, [type]);

  const changePage = (page: number) => {
    GetImages(type, page, PER_PAGE);
  };

  const handleDeleteSubmit = () => {
    if (deletingImage === null) {
      return;
    }
    destroyImage(deletingImage.id, () => {
      GetImages(type, currentPage, PER_PAGE);
      setDeletingImage(null);
    });
  };

  // システム画像とブロック画像お互い切り替える時に、画像コンテキストをリセットします。

  useEffect(() => {
    setInputValidationErrorMessage(null);
    reset();
    GetImages(type, 1, PER_PAGE);
  }, [type, GetImages, reset]);

  return (
    <main className="content">
      <div className="container-fluid p-0">
        <div className="mb-3">
          <h3 className="h3 d-inline align-middle">画像管理</h3>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="h4 float-start mt-2">{title}</h4>
                <div className="float-end mr-1">
                  {(validationError || inputValidationErrorMessage) ? (
                    <span className="text-danger px-2">
                      {validationError || inputValidationErrorMessage}
                    </span>
                  ) : (
                    <span className="px-2">
                      {/* max file size for image is {env value} MB  */}
                        {`画像最大ファイルサイズは${config.image.maxFileSizeInMb}MBです。` }
                    </span>
                  )}
                  <Button
                    className="btn btn-primary"
                    variant="contained"
                    component="label"
                    sx={{
                      position: 'relative',
                    }}
                  >
                    画像をアップロードする ...
                    <InputImage
                      type={type}
                      id="id"
                      isDisabled={false}
                      fileHandler={() => {
                        setInputValidationErrorMessage(null);
                        GetImages(type, 1, PER_PAGE);
                      }}
                      onMaxFileSizeExceeded={(message) => {
                        setInputValidationErrorMessage(message);
                        setTimeout(() => {
                          setInputValidationErrorMessage(null);
                        }, 10000);
                      }}
                    />
                  </Button>
                </div>
              </div>
              <div className="card-body">
                <div className="image-list">
                  {apiError && (
                    <div className="alert alert-danger">
                      {apiError}
                    </div>
                  )}
                  {images.map((image) => (
                    <div className="card" key={image.id}>
                      <div className="card-body">
                        <div className="image-image">
                          <Img src={image.url} alt="" />
                        </div>
                      </div>
                      <div className="image-btn-top-right hover-view">
                        <button
                          className="block-circle-btn-delete"
                          data-bs-toggle="modal"
                          data-bs-target="#blockdeleteModal"
                          type="button"
                          onClick={() => setDeletingImage(image)}
                        >
                          <i className="fa-regular fa-trash-can fa-fw" />
                        </button>
                      </div>
                    </div>
                  ))}

                </div>
                <div className="row mt-1 block-list-footer">
                  <div className="col-6">
                    <div className="paging-info d-flex justify-content-start align-items-center mt-2">
                      <div>
                        {' '}
                        全
                        {' '}
                        {total}
                        {' '}
                        件中
                        {' '}
                        {fromItem}
                        {' '}
                        〜
                        {toItem}
                        {' '}
                        件を表示
                      </div>
                    </div>
                  </div>

                  <div className="col-6 d-flex justify-content-end mt-2">
                    <div>
                      <Paginator currentPage={currentPage} changePage={changePage} links={links} />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
      <DeleteModal
        title="画像削除"
        open={deletingImage != null}
        handleClose={() => setDeletingImage(null)}
        handleSave={handleDeleteSubmit}
        desc="この画像は他で使用されている可能性があります。 本当に画像を削除してもよろしいですか？"
      />
    </main>
  );
};

export default ImagesManagement;
